import React, { useEffect, useState } from "react";

import styled, { keyframes } from "styled-components";
import { Container, Row, Col } from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";
import ArrowCircleLeftIcon from "@mui/icons-material/ArrowCircleLeft";

import { D1, GradientSpan, P3 } from "../../../components/theme/text/Headings";

import {
  Blue,
  White,
  OffWhite,
  DarkGrey,
  gradientColors,
  Purple,
  Red,
  White05,
  Pink,
} from "../../../components/theme/colors";
import { TextField, InputAdornment, CircularProgress } from "@mui/material";
import { Clear } from "@mui/icons-material";
import { ButtonGradient } from "../../../components/theme/Button";
import { useAuth } from "../../../services/AuthContext";
import { db } from "../../../services/firebase.config";
import {
  doc,
  getDoc,
  setDoc,
  collection,
  query,
  where,
  getDocs,
} from "firebase/firestore";
import { validateUsername } from "../../../utils/reservedUsernames";
import { useNavigate } from "react-router-dom";
import mixpanelService from "../../../services/MixpanelService";
import postHogService from "../../../services/PostHogService";
import {
  ProgressBarContainer,
  ProgressBarProgress,
} from "../../../components/theme/QuizBox";
import LayoutOnboarding from "../../../components/layout/layoutOnboarding";

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const TopContent = styled.div`
  margin-top: 6vh;
  padding: 0 15px;
  animation: ${fadeInUp} 0.7s ease-out;
`;

const HeaderRow = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  min-height: 48px;
  padding: 0px 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const BottomLogin = styled.div`
  position: absolute;
  bottom: 10vh;
  left: 2vh;
  right: 2vh;
`;

const ErrorText = styled.div`
  color: ${Red};
  font-size: 14px;
  margin-top: 8px;
  text-align: left;
  padding-left: 5px;
`;

const GradientCheckIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <defs>
      <linearGradient id="grad-check" x1="0%" y1="0%" x2="100%" y2="0%">
        <stop offset="0%" stopColor="#ff5faf" />
        <stop offset="100%" stopColor="#5b7bff" />
      </linearGradient>
    </defs>
    <path
      d="M9.00016 16.1701L4.83016 12.0001L3.41016 13.4101L9.00016 19.0001L21.0002 7.00012L19.5902 5.58012L9.00016 16.1701Z"
      fill="url(#grad-check)"
    />
  </svg>
);

const OnboardingTwo = () => {
  const navigate = useNavigate();
  const { currentUserState } = useAuth();
  const [username, setUserName] = useState("");
  const [usernameError, setUsernameError] = useState("");
  const [isCheckingUsername, setIsCheckingUsername] = useState(false);

  // Track page view on mount
  useEffect(() => {
    const props = {
      user_id: currentUserState?.uid || "anonymous",
      step: "username",
      timestamp: new Date().toISOString(),
    };
    mixpanelService.track("Onboarding Username Viewed", props);
    postHogService.track("Onboarding Username Viewed", props);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const fetchProfileData = async () => {
      if (!currentUserState) return;

      try {
        const profileDocRef = doc(db, "public-profiles", currentUserState.uid);
        const profileDoc = await getDoc(profileDocRef);

        if (profileDoc.exists()) {
          const data = profileDoc.data();
          setUserName(data.username || "");
        }
      } catch (error) {
        // Silently fail - profile data is optional during onboarding
      }
    };

    fetchProfileData();
  }, [currentUserState]);

  const handleBack = async () => {
    navigate("/onboarding-one-five");
  };

  const checkUsernameAvailability = async (usernameToCheck) => {
    if (!usernameToCheck || usernameToCheck.trim() === "") {
      setUsernameError("");
      return true;
    }

    const trimmedUsername = usernameToCheck.trim();

    // First validate username format (spaces, reserved words, etc.)
    const validationError = validateUsername(trimmedUsername);
    if (validationError) {
      setUsernameError(validationError);
      return false;
    }

    setIsCheckingUsername(true);
    try {
      // Query all documents in public-profiles collection to check for existing username
      // Convert to lowercase for consistent comparison
      const profilesQuery = query(
        collection(db, "public-profiles"),
        where("username", "==", trimmedUsername.toLowerCase())
      );
      const querySnapshot = await getDocs(profilesQuery);

      // If any document exists with this username, and it's not the current user's document
      if (!querySnapshot.empty) {
        const existingDoc = querySnapshot.docs[0];
        if (existingDoc.id !== currentUserState?.uid) {
          setUsernameError("This username is already taken.");
          return false;
        }
      }

      setUsernameError("");
      return true;
    } catch (error) {
      console.error("Error checking username availability:", error);
      setUsernameError(
        "Error checking username availability. Please try again."
      );
      return false;
    } finally {
      setIsCheckingUsername(false);
    }
  };

  const handleUsernameChange = (e) => {
    const newUsername = e.target.value;
    setUserName(newUsername);

    // Clear error when user starts typing
    if (usernameError) {
      setUsernameError("");
    }
  };

  const handleUsernameBlur = () => {
    if (username.trim()) {
      checkUsernameAvailability(username);
    }
  };

  const handleUpdateProfile = async () => {
    if (!currentUserState) {
      alert("You need to be logged in to update your profile");
      return;
    }

    // Check username availability before saving
    const isUsernameAvailable = await checkUsernameAvailability(username);
    if (!isUsernameAvailable) {
      return;
    }

    const profileDocRef = doc(db, "public-profiles", currentUserState.uid);

    try {
      await setDoc(
        profileDocRef,
        {
          uid: currentUserState.uid,
          username: username.trim().toLowerCase(),
        },
        { merge: true } // Merge to add to the same document
      );

      navigate("/onboarding-three");
    } catch (error) {
      console.error("Error adding answer: ", error);
    }
  };

  const getEndAdornment = () => {
    if (isCheckingUsername) {
      return (
        <InputAdornment position="end">
          <CircularProgress size={20} sx={{ color: OffWhite }} />
        </InputAdornment>
      );
    }

    if (usernameError) {
      return (
        <InputAdornment position="end">
          <Clear sx={{ color: Red }} />
        </InputAdornment>
      );
    }

    if (username.trim() && !usernameError && !isCheckingUsername) {
      return (
        <InputAdornment position="end">
          <GradientCheckIcon />
        </InputAdornment>
      );
    }

    return null;
  };
  return (
    <LayoutOnboarding>
      <ProgressBarContainer style={{ marginBottom: 8 }}>
        <ProgressBarProgress
          $background={Blue}
          $transitionBackground={Purple}
          width={`70%`}
        />
      </ProgressBarContainer>
      <HeaderRow>
        <div
          style={{
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
            width: "40px",
            height: "40px",
          }}
          onClick={handleBack}
        >
          <ArrowCircleLeftIcon
            style={{
              color: White05,
              fontSize: "28px",
            }}
          />
        </div>
      </HeaderRow>
      <Container>
        <Row>
          <Col
            lg={{ offset: 4, span: 4 }}
            md={{ offset: 3, span: 6 }}
            sm={{ offset: 2, span: 8 }}
            xs={{ span: 12 }}
            className="text-align-center"
          >
            <TopContent>
              <D1 color={White} className="mb-50">
                Choose a{" "}
                <GradientSpan $background={Pink} $transitionBackground={Blue}>
                  username.
                </GradientSpan>
              </D1>
              <TextField
                fullWidth
                label="your username"
                variant="outlined"
                value={username}
                onChange={handleUsernameChange}
                onBlur={handleUsernameBlur}
                disabled={isCheckingUsername}
                InputProps={{
                  endAdornment: getEndAdornment(),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: DarkGrey,
                    borderRadius: "15px",
                    "& .MuiOutlinedInput-input": {
                      fontSize: "17px",
                      color: OffWhite,
                      padding: "15px 30px",
                    },
                  },
                  "& .MuiInputLabel-root": {
                    fontSize: "17px",
                    color: OffWhite,
                    padding: "0px 20px",
                  },
                  "& .Mui-focused": {
                    color: OffWhite,
                  },
                }}
              />
              {usernameError && <ErrorText>{usernameError}</ErrorText>}
              <P3
                color={White05}
                className="mt-30"
                style={{ textAlign: "center" }}
              >
                By continuing, you agree to our{" "}
                <a
                  href="https://www.vibecheckme.com/terms-of-service"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: White05,
                    textDecoration: "underline",
                  }}
                >
                  Terms of Service
                </a>{" "}
                <br />
                and{" "}
                <a
                  href="https://www.vibecheckme.com/privacy-policy"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: White05,
                    textDecoration: "underline",
                  }}
                >
                  Privacy Policy
                </a>
                .
              </P3>
            </TopContent>
            <BottomLogin>
              <div style={{ padding: "0 15px" }}>
                <ButtonGradient
                  width="100%"
                  className="mt-20"
                  $background={gradientColors["personality-check"].background}
                  $transitionBackground={
                    gradientColors["personality-check"].transitionBackground
                  }
                  onClick={handleUpdateProfile}
                  disabled={
                    isCheckingUsername || !!usernameError || !username.trim()
                  }
                >
                  {isCheckingUsername ? "Checking..." : "Continue"}
                </ButtonGradient>
              </div>
            </BottomLogin>
          </Col>
        </Row>
      </Container>
    </LayoutOnboarding>
  );
};

export default OnboardingTwo;
